import { useEffect, useId, useRef, useState } from "react";
import { useAccessibilityPrefs } from "../../utils/AccessibilityContext";
import "./AccessibilityMenu.css";

const PREFERENCES = [
  { key: "largerText", label: "larger text" },
  { key: "higherContrast", label: "higher contrast" },
  { key: "reduceMotion", label: "reduce motion" },
  { key: "underlineLinks", label: "underline links" },
];

const AccessibilityMenu = () => {
  const { prefs, setPref, resetPrefs } = useAccessibilityPrefs();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const lastScrollY = useRef(0);
  const groupId = useId();
  const labelId = `${groupId}-label`;
  const hasNonDefault = Object.values(prefs).some(Boolean);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    if (window.scrollY > 24) setIsCollapsed(true);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 24) {
        setIsCollapsed(false);
      } else if (currentScrollY > lastScrollY.current + 2) {
        setIsCollapsed(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className={`a11y-dock${isCollapsed ? " is-collapsed" : ""}`}>
      <button
        type="button"
        className="a11y-dock-toggle"
        onClick={() => setIsCollapsed((current) => !current)}
        aria-expanded={!isCollapsed}
        aria-controls={`${groupId}-panel`}
        aria-label={isCollapsed ? "Show accessibility preferences" : "Hide accessibility preferences"}
      >
        <span aria-hidden="true">{isCollapsed ? "‹" : "›"}</span>
      </button>
      <div
        id={`${groupId}-panel`}
        className="a11y-dock-panel"
        role="group"
        aria-labelledby={labelId}
        aria-hidden={isCollapsed}
        inert={isCollapsed ? "" : undefined}
      >
        <p id={labelId} className="a11y-dock-label">accessibility</p>
        <ul className="a11y-dock-list">
        {PREFERENCES.map((item, index) => {
          const inputId = `${groupId}-${item.key}`;
          return (
            <li key={item.key} className="a11y-dock-row">
              <span className="a11y-dock-index" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <label className="a11y-dock-field" htmlFor={inputId}>
                <span className="a11y-dock-text">{item.label}</span>
                <span className="a11y-dock-control">
                  <input
                    type="checkbox"
                    id={inputId}
                    checked={prefs[item.key]}
                    onChange={(event) => setPref(item.key, event.target.checked)}
                  />
                  <span className="a11y-dock-dot" aria-hidden="true" />
                </span>
              </label>
            </li>
          );
        })}
        </ul>
        <button
          type="button"
          className="a11y-dock-reset"
          onClick={resetPrefs}
          disabled={!hasNonDefault}
        >
          reset preferences
        </button>
      </div>
    </div>
  );
};

export default AccessibilityMenu;
